import hotkeys from "hotkeys-js";

/**
 * Registers an event listener for the 'keydown' event and triggers a callback function when the 'Escape' key is pressed.
 *
 * @param callback A function to be called when the 'Escape' key is pressed.
 * @returns A function that removes the event listener for the 'keydown' event.
 */
export function onEscPress(callback: () => void): () => void {
  const evaluateKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape" || event.key === "Esc" || event.keyCode === 27) {
      callback();
    }
  };

  addEventListener("keydown", evaluateKeyDown);

  return () => {
    removeEventListener("keydown", evaluateKeyDown);
  };
}

/**
 * Keyboard shortcut configuration for hotkeys-js
 */
export type KeyboardShortcut = {
  /** Hotkey combination (e.g., "cmd+k", "ctrl+k", "cmd+o") */
  keys: string;
  /** Action to execute when the shortcut is triggered */
  action: () => void;
  /** Optional description for the shortcut */
  description?: string;
  /** Whether to prevent default browser behavior (default: true) */
  preventDefault?: boolean;
};

/**
 * Registers keyboard shortcuts using hotkeys-js and returns a cleanup function
 *
 * @param shortcuts Array of keyboard shortcuts to register
 * @param scope Optional scope for the shortcuts (default: "all")
 * @returns A function that removes all registered shortcuts
 */
export function onKeyboardShortcuts(shortcuts: KeyboardShortcut[], scope: string = "all"): () => void {
  shortcuts.forEach(({ keys, action, preventDefault = true }) => {
    hotkeys(keys, { scope }, (event: KeyboardEvent) => {
      if (preventDefault) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }

      action();
    });
  });

  return () => {
    shortcuts.forEach(({ keys }) => {
      hotkeys.unbind(keys, scope);
    });
  };
}
